import { registerUploadFilesApi, getUploadFileUrlsApi, getUploadPartUrlsApi, updateUploadFileStatusApi, restartUploadFileApi, reportUploadFileProgressApi, UPLOAD_TYPE } from './apiServices';
import { updateLiveUpload, withUploadLock, LIVE_STATUS } from './liveUploads';

// Files are registered and signed one batch at a time so signed URLs are fresh when used
// and a 10k-file upload never fires thousands of API calls at once
const BATCH_SIZE = 500;
const UPLOAD_CONCURRENCY = 6;
// Large (multipart) files: parts uploaded in parallel per file, and part URLs signed in
// small groups so they don't expire before use
const PART_CONCURRENCY = 4;
const PART_SIGN_BATCH = 20;
const MAX_ATTEMPTS = 3;
// The complete call can report missing parts; re-upload them at most this many times
const MAX_COMPLETE_ROUNDS = 3;
// Refresh a signed URL this long before its expires_at rather than letting S3 reject it
const URL_EXPIRY_MARGIN_MS = 60 * 1000;
// Register statuses meaning the file is already uploaded in this session
const DONE_STATUSES = ['COMPLETED', 'UPLOADED'];
// Byte progress fires many times a second; publish to the UI at most this often
const PUBLISH_INTERVAL_MS = 300;
// Speed is smoothed over samples at least this far apart so it doesn't jitter
const SPEED_SAMPLE_MS = 1000;
// Bytes go straight to storage, so without a heartbeat the server can go minutes without hearing from
// an upload that is still running. Each in-flight file reports its progress this often, which keeps
// its updated_at fresh for other tabs, devices and users.
const HEARTBEAT_INTERVAL_MS = 30 * 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const missingPartsOf = (error) => (error && error.data && Array.isArray(error.data.missing_parts) ? error.data.missing_parts.map(Number) : []);

// Exponential backoff with jitter: ~1s, ~2s, ~4s. A missing_parts answer isn't transient,
// so it's handed straight back to the caller instead of being retried.
const withRetry = async (task) => {
  let lastError;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await task(attempt);
    } catch (error) {
      lastError = error;
      if (missingPartsOf(error).length) throw error;
      if (attempt < MAX_ATTEMPTS - 1) await sleep(1000 * 2 ** attempt + Math.random() * 300);
    }
  }
  throw lastError;
};

const isExpiring = (signed) => !!signed.expiresAt && Date.now() > signed.expiresAt - URL_EXPIRY_MARGIN_MS;

// XHR rather than fetch because only XHR reports upload progress. It also never carries the
// app's Authorization header, which S3 rejects on presigned requests.
const putToStorage = ({ url, method = 'PUT', headers = {} }, body, onBytes) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open(method, url);
  Object.keys(headers).forEach(name => xhr.setRequestHeader(name, headers[name]));
  xhr.upload.onprogress = (evt) => onBytes(evt.loaded);
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      resolve();
    } else {
      const error = new Error(`Storage returned HTTP ${xhr.status}`);
      error.status = xhr.status;
      reject(error);
    }
  };
  xhr.onerror = () => reject(new Error('Network error while uploading'));
  xhr.send(body);
});

// Runs tasks with at most `concurrency` at once. After a task fails no new ones start, the ones already
// running are allowed to finish, and then the first error is thrown. Rejecting straight away would leave
// the other workers uploading in the background (e.g. more parts of a file already marked FAILED).
export const runPool = async (tasks, concurrency) => {
  let cursor = 0;
  let firstError = null;
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
    while (cursor < tasks.length && !firstError) {
      const task = tasks[cursor++];
      try {
        await task();
      } catch (error) {
        if (!firstError) firstError = error;
      }
    }
  });
  await Promise.all(workers);
  if (firstError) throw firstError;
};

const chunk = (list, size) => {
  const chunks = [];
  for (let i = 0; i < list.length; i += size) chunks.push(list.slice(i, i + size));
  return chunks;
};

const toFileMeta = ({ file, relativePath }) => ({
  name: file.name,
  size: file.size,
  type: file.type || 'application/octet-stream',
  path: relativePath,
});

/**
 * Uploads items ({ file, relativePath, restart? }) into an existing upload session.
 * restart: true marks a file that changed since it was registered (different size or lastModified);
 * its uploaded data is discarded first so old and new bytes are never mixed.
 * Files under 100 MB go up in one PUT; larger files go up in parts (server decides via upload_type).
 * Re-running it for the same upload_id is safe: the server matches files by path, completed files
 * are skipped, failed ones are reset to PENDING, and large files continue from parts_done.
 * Never rejects: per-file failures are collected and returned so one bad file
 * doesn't stop the rest of the upload.
 * Only one tab uploads a session at a time: if another tab is already uploading it, nothing is sent
 * and the result has alreadyRunning: true.
 */
export const runStreamUpload = (params) => withUploadLock(params.uploadId, () => uploadSession(params), () => ({
  totalFiles: params.items.length,
  totalBytes: params.items.reduce((sum, item) => sum + item.file.size, 0),
  completedFiles: 0,
  failedFiles: 0,
  skippedFiles: 0,
  uploadedBytes: 0,
  failures: [],
  skipped: [],
  alreadyRunning: true,
}));

const uploadSession = async ({ assetType, assetSlug, uploadId, items, onProgress }) => {
  const api = { assetType, assetSlug, uploadId };
  const progress = {
    totalFiles: items.length,
    totalBytes: items.reduce((sum, item) => sum + item.file.size, 0),
    completedFiles: 0,
    failedFiles: 0,
    skippedFiles: 0,
    uploadedBytes: 0,
  };
  const failures = [];
  const skipped = [];
  // Bytes sent so far for files still in flight, so the bar moves within large files
  const inFlight = new Map();
  const startedAt = Date.now();
  let speed = 0;
  let speedSample = { at: startedAt, bytes: 0 };
  let lastPublish = 0;

  const snapshot = () => {
    let inFlightBytes = 0;
    inFlight.forEach(bytes => { inFlightBytes += bytes; });
    return { ...progress, uploadedBytes: progress.uploadedBytes + inFlightBytes };
  };
  const publish = (force = false) => {
    const now = Date.now();
    if (!force && now - lastPublish < PUBLISH_INTERVAL_MS) return;
    lastPublish = now;
    const current = snapshot();
    if (now - speedSample.at >= SPEED_SAMPLE_MS) {
      const instant = Math.max(0, current.uploadedBytes - speedSample.bytes) / ((now - speedSample.at) / 1000);
      speed = speed ? speed * 0.7 + instant * 0.3 : instant;
      speedSample = { at: now, bytes: current.uploadedBytes };
    }
    updateLiveUpload(uploadId, { ...current, speed, status: LIVE_STATUS.UPLOADING, startedAt });
  };
  const report = () => {
    publish();
    if (onProgress) onProgress(snapshot());
  };
  const fail = (item, reason) => {
    progress.failedFiles += 1;
    failures.push({ path: item.relativePath, error: reason || 'Upload failed' });
  };
  const complete = (item) => {
    progress.completedFiles += 1;
    progress.uploadedBytes += item.file.size;
  };

  const fetchFileInfo = async (fileId) => {
    const fresh = await getUploadFileUrlsApi({ ...api, fileIds: [fileId] });
    if (!fresh.urls[fileId]) throw new Error('No upload URL returned');
    return fresh.urls[fileId];
  };

  // Whole file in one PUT, with the server's headers (the URL signs content-type).
  // Every retry gets a fresh URL.
  const uploadSingle = async (fileId, item, initial) => {
    let signed = initial;
    await withRetry(async (attempt) => {
      if (attempt > 0 || isExpiring(signed)) signed = await fetchFileInfo(fileId);
      inFlight.set(fileId, 0);
      try {
        await putToStorage({ url: signed.url, method: signed.method, headers: signed.headers || {} }, item.file, (bytes) => {
          inFlight.set(fileId, bytes);
          report();
        });
      } catch (error) {
        inFlight.set(fileId, 0);
        report();
        throw error;
      }
    });
  };

  // Uploads the given part numbers of a multipart file. Parts already on the server count as sent.
  // Part n = file.slice((n - 1) * partSize, n * partSize), PUT with no headers.
  const uploadParts = async (fileId, item, info, partNumbers, partBytes) => {
    const { partSize } = info;
    const sizeOf = (n) => Math.max(0, Math.min(partSize, item.file.size - (n - 1) * partSize));
    const publishFile = () => {
      let sent = 0;
      partBytes.forEach(bytes => { sent += bytes; });
      inFlight.set(fileId, sent);
      report();
    };
    partNumbers.forEach(n => partBytes.set(n, 0));
    publishFile();

    for (const group of chunk(partNumbers, PART_SIGN_BATCH)) {
      let urls = await withRetry(() => getUploadPartUrlsApi({ ...api, fileId, partNumbers: group }));
      await runPool(group.map(n => async () => {
        const blob = item.file.slice((n - 1) * partSize, n * partSize);
        await withRetry(async (attempt) => {
          // Every retry, a missing URL, or one close to expiry gets a freshly signed URL
          if (attempt > 0 || !urls[n] || isExpiring(urls[n])) {
            urls = { ...urls, ...(await getUploadPartUrlsApi({ ...api, fileId, partNumbers: [n] })) };
            if (!urls[n]) throw new Error(`No upload URL returned for part ${n}`);
          }
          try {
            await putToStorage({ url: urls[n].url }, blob, (bytes) => {
              partBytes.set(n, bytes);
              publishFile();
            });
            partBytes.set(n, sizeOf(n));
            publishFile();
          } catch (error) {
            partBytes.set(n, 0);
            publishFile();
            throw new Error(`Part ${n} failed: ${error.message}`);
          }
        });
      }), PART_CONCURRENCY);
    }
  };

  const uploadMultipart = async (fileId, item, info, partBytes) => {
    const done = new Set(info.partsDone);
    const sizeOf = (n) => Math.max(0, Math.min(info.partSize, item.file.size - (n - 1) * info.partSize));
    done.forEach(n => partBytes.set(n, sizeOf(n)));
    const todo = [];
    for (let n = 1; n <= info.totalParts; n++) if (!done.has(n)) todo.push(n);
    await uploadParts(fileId, item, info, todo, partBytes);
  };

  // COMPLETED is safe to send twice. For multipart files the server may answer with
  // missing_parts; those are uploaded again and COMPLETED is re-sent.
  const markCompleted = async (fileId, item, info, partBytes) => {
    for (let round = 0; round < MAX_COMPLETE_ROUNDS; round++) {
      let missing = [];
      try {
        const data = await withRetry(() => updateUploadFileStatusApi({ ...api, fileId, status: 'COMPLETED' }));
        missing = data && Array.isArray(data.missing_parts) ? data.missing_parts.map(Number) : [];
        if (!missing.length) return;
      } catch (error) {
        missing = missingPartsOf(error);
        if (!missing.length || info.uploadType !== UPLOAD_TYPE.MULTIPART) throw error;
      }
      if (info.uploadType !== UPLOAD_TYPE.MULTIPART) throw new Error('Server reported missing parts for a single-part file');
      await uploadParts(fileId, item, info, missing, partBytes);
    }
    throw new Error('Some parts are still missing after re-uploading them');
  };

  const uploadOne = async (fileId, item, initialInfo) => {
    let stage = 'upload';
    try {
      const info = initialInfo || await withRetry(() => fetchFileInfo(fileId));
      const partBytes = new Map();
      if (info.uploadType === UPLOAD_TYPE.MULTIPART) await uploadMultipart(fileId, item, info, partBytes);
      else await uploadSingle(fileId, item, info);
      // The bytes are in storage, but the file only appears in the folder once the server
      // marks it completed, so it isn't counted as done until that call succeeds
      stage = 'complete';
      await markCompleted(fileId, item, info, partBytes);
      inFlight.delete(fileId);
      complete(item);
    } catch (error) {
      inFlight.delete(fileId);
      if (stage === 'complete') {
        fail(item, `Uploaded, but the server could not be updated: ${error.message}`);
      } else {
        fail(item, error.message);
        // Best effort: the file already counts as failed locally even if this report is lost
        updateUploadFileStatusApi({ ...api, fileId, status: 'FAILED', errorMessage: error.message }).catch(() => {});
      }
    }
    report();
  };

  publish(true);
  // A missed heartbeat is harmless; the next one (or the file finishing) refreshes updated_at
  const heartbeat = setInterval(() => {
    inFlight.forEach((bytes, fileId) => {
      reportUploadFileProgressApi({ ...api, fileId, uploadedBytes: bytes }).catch(() => {});
    });
  }, HEARTBEAT_INTERVAL_MS);
  try {
    for (let offset = 0; offset < items.length; offset += BATCH_SIZE) {
      const batch = items.slice(offset, offset + BATCH_SIZE);

      let registered;
      try {
        registered = await withRetry(() => registerUploadFilesApi({ ...api, files: batch.map(toFileMeta) }));
      } catch (error) {
        batch.forEach(item => fail(item, error.message));
        report();
        continue;
      }

      // Match server rows back to local files by path, or by name when it is unique in the batch
      const byPath = new Map(registered.files.filter(row => row.path).map(row => [row.path, row]));
      const nameCounts = batch.reduce((counts, item) => ({ ...counts, [item.file.name]: (counts[item.file.name] || 0) + 1 }), {});
      const byName = new Map(registered.files.filter(row => row.name && nameCounts[row.name] === 1).map(row => [row.name, row]));
      const rejected = new Map(registered.rejected.filter(entry => entry.id).map(entry => [entry.id, entry.reason]));

      const toUpload = [];
      batch.forEach(item => {
        const row = byPath.get(item.relativePath) || byName.get(item.file.name);
        if (row && row.fileId) {
          if (DONE_STATUSES.includes(row.status)) {
            complete(item);
          } else {
            // A failed file is reset to PENDING, and a changed one restarted, before it is signed again
            toUpload.push({ item, fileId: row.fileId, resetFailed: row.status === 'FAILED', restart: !!item.restart });
          }
        } else {
          const reason = rejected.get(item.relativePath) || rejected.get(item.file.name);
          fail(item, reason ? `Rejected: ${reason}` : rejected.size ? 'Rejected by server' : 'File was not registered');
        }
      });
      report();

      const ready = [];
      await runPool(toUpload.map(pair => async () => {
        try {
          if (pair.resetFailed) await withRetry(() => updateUploadFileStatusApi({ ...api, fileId: pair.fileId, status: 'PENDING' }));
          if (pair.restart) await withRetry(() => restartUploadFileApi({ ...api, fileId: pair.fileId, fileSize: pair.item.file.size }));
          ready.push(pair);
        } catch (error) {
          fail(pair.item, `Could not reset the file for upload: ${error.message}`);
        }
      }), UPLOAD_CONCURRENCY);
      report();
      if (!ready.length) continue;

      let signedBatch;
      try {
        signedBatch = await withRetry(() => getUploadFileUrlsApi({ ...api, fileIds: ready.map(pair => pair.fileId) }));
      } catch (error) {
        ready.forEach(pair => fail(pair.item, error.message));
        report();
        continue;
      }
      const skippedIds = new Map(signedBatch.skipped.filter(entry => entry.id).map(entry => [entry.id, entry.reason]));

      await runPool(ready.map(({ item, fileId }) => async () => {
        if (skippedIds.has(fileId)) {
          progress.skippedFiles += 1;
          skipped.push({ path: item.relativePath, reason: skippedIds.get(fileId) || 'Skipped by server' });
          report();
          return;
        }
        await uploadOne(fileId, item, signedBatch.urls[fileId]);
      }), UPLOAD_CONCURRENCY);
    }
  } finally {
    clearInterval(heartbeat);
    updateLiveUpload(uploadId, { ...snapshot(), speed: 0, status: LIVE_STATUS.DONE, startedAt, finishedAt: Date.now() });
  }
  return { ...progress, failures, skipped };
};
